import React from 'react';
import { useTranslation } from "react-i18next";
import { 
  Play, 
  Pause, 
  Minus, 
  Plus, 
  Download,
  Mic,
  FileAudio,
  Square,
  Gauge,
  Type,
  AlignJustify,
  ArrowRightLeft,
  Ear,
  ChevronUp,
  Settings2,
  Trash2,
  Save,
  Captions,
  Volume2,
  ChevronDown,
  Circle,
  Loader2,
  Check,
  HardDrive,
  Cloud,
  X,
  FileText,
  CloudUpload,
  Pin
} from 'lucide-react';
import { RecordingState, AudioFormat, AudioQuality } from '@/types/recording';
import { useToast } from '@/hooks/use-toast';
import { useRecordings } from '@/hooks/useRecordings';
import { useFFmpeg } from '@/hooks/useFFmpeg';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MiniProgressRing, ProgressStepIndicator } from './ProgressStepIndicator';

interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

interface UnifiedControlBarProps {
  // 提詞器控制
  highlightDuration: number;
  fontSize: number;
  lineHeight: number;
  horizontalPadding: number;
  menuOpacity: number;
  onHighlightDurationChange: (value: number) => void;
  onFontSizeChange: (delta: number) => void;
  onLineHeightChange: (value: number) => void;
  onHorizontalPaddingChange: (value: number) => void;
  onMenuOpacityChange: (value: number) => void;
  
  // 播放控制
  isPlaying: boolean;
  onTogglePlay: () => void;
  
  // 錄音控制
  recordingState: RecordingState;
  stream: MediaStream | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  
  // 麥克風
  devices: MicrophoneDevice[];
  selectedDeviceId: string | null;
  selectedDeviceLabel: string;
  onMicrophoneChange: (deviceId: string) => void;
  
  // 格式與下載
  audioFormat: AudioFormat;
  audioQuality: AudioQuality;
  onFormatChange: (format: AudioFormat) => void;
  onQualityChange: (quality: AudioQuality) => void;
  onDownloadAudio: () => void;
  onPreviewRecording?: () => void;
  onDiscardRecording?: () => void;
  hasRecording?: boolean;
  audioBlob?: Blob | null;  // Combined blob for audio playback
  recordingDuration?: number;  // Duration for display
  isConverting?: boolean;
  isExpanded?: boolean; // New prop to control visibility mode
  
  // Mobile Tab & Menu Control (Optional - for lifting state)
  activeMobileTab?: 'teleprompter' | 'audio';
  onActiveMobileTabChange?: (tab: 'teleprompter' | 'audio') => void;
  isMobileMenuOpen?: boolean;
  onMobileMenuOpenChange?: (isOpen: boolean) => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
}

// 控制項組件 - Desktop Neobrutalism
function ControlItem({
  icon: Icon,
  label,
  value,
  onDecrease,
  onIncrease,
  minDisabled,
  maxDisabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
  minDisabled?: boolean;
  maxDisabled?: boolean;
}) {
  return (
    <div className="w-full h-[36px] flex items-center px-2">
      {/* 整個控制項在一個白色邊框容器內 - 添加 overflow-hidden 防止溢出 */}
      <div className="w-full flex items-center gap-2 bg-black border-2 border-white rounded-full px-3 h-[34px] shadow-[2px_2px_0_white] overflow-hidden">
        {/* 圖標 */}
        <Icon className="w-4 h-4 text-[#FF6B9D] shrink-0" />
        {/* 標籤 - 允許截斷 */}
        <span className="text-zinc-400 text-[10px] font-bold uppercase tracking-wider truncate min-w-0">{label}</span>
        {/* 控制區 - 放在右側，不收縮 */}
        <div className="ml-auto flex items-center gap-0.5 shrink-0">
          <button
            onClick={onDecrease}
            disabled={minDisabled}
            className="
              w-6 h-6 flex items-center justify-center
              text-zinc-400 hover:text-white hover:bg-zinc-800
              rounded-md
              disabled:opacity-30 disabled:cursor-not-allowed
              transition-all
            "
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="text-white font-mono text-[12px] w-[50px] text-center font-bold">
            {value}
          </span>
          <button
            onClick={onIncrease}
            disabled={maxDisabled}
            className="
              w-6 h-6 flex items-center justify-center
              text-zinc-400 hover:text-white hover:bg-zinc-800
              rounded-md
              disabled:opacity-30 disabled:cursor-not-allowed
              transition-all
            "
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// 選擇按鈕 - 無邊框
function SelectButton({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full h-[34px] relative flex items-center justify-center gap-2.5 px-3
        bg-zinc-900 border-2 border-white
        rounded-lg
        text-xs font-bold text-white
        hover:bg-zinc-800
        shadow-[2px_2px_0_white]
        active:translate-y-0.5 active:shadow-none
        transition-all
        disabled:opacity-50 disabled:cursor-not-allowed
      `}
    >
      <Icon className="w-3.5 h-3.5 text-[#FF6B9D] shrink-0" />
      <span className="truncate whitespace-nowrap">{label}</span>
      <ChevronDown className="w-3 h-3 text-zinc-500 shrink-0 absolute right-3" />
    </button>
  );
}

// Mobile-optimized control item (Vertical Stack - Neobrutalism Design)
function MobileControlItem({
  icon: Icon,
  label,
  value,
  onDecrease,
  onIncrease,
  minDisabled,
  maxDisabled,
  color = "#fff"
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
  minDisabled?: boolean;
  maxDisabled?: boolean;
  color?: string;
}) {
  return (
    <div className="flex flex-col gap-3 bg-zinc-900 p-3 rounded-2xl border-2 border-zinc-800 hover:border-zinc-700 transition-colors group">
      <div className="flex items-center gap-2 px-1">
        <div style={{ color }} className="opacity-90">
             <Icon className="w-4 h-4" />
        </div>
        <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      
      <div className="flex items-center justify-between bg-black/40 rounded-xl p-1 w-full border border-white/5">
         <button 
           onClick={onDecrease} 
           disabled={minDisabled}
           className="w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-90"
          > 
            <Minus className="w-4 h-4" /> 
         </button>
         
         <span className="text-sm font-mono font-bold text-white tabular-nums flex-1 text-center select-none">
            {value}
         </span>
         
         <button 
           onClick={onIncrease} 
           disabled={maxDisabled}
           className="w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-90"
          > 
            <Plus className="w-4 h-4" /> 
         </button>
      </div>
    </div>
  );
}

// Mobile Horizontal Row Control (Single row with icon, label, value, and +/- buttons)
function MobileControlRow({
  icon: Icon,
  label,
  value,
  onDecrease,
  onIncrease,
  minDisabled,
  maxDisabled,
  color = "#fff"
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
  minDisabled?: boolean;
  maxDisabled?: boolean;
  color?: string;
}) {
  return (
    <div className="flex items-center justify-center gap-3 bg-zinc-900 px-3 py-2.5 rounded-xl border-2 border-zinc-800">
      {/* Icon */}
      <div style={{ color }} className="opacity-90 shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      
      {/* Label */}
      <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider w-[48px] shrink-0 leading-tight">
        {label}
      </span>
      
      {/* Controls - fixed max width for narrower appearance */}
      <div className="flex items-center gap-1 bg-black/40 rounded-lg p-0.5 border border-white/5 max-w-[180px]">
        <button 
          onClick={onDecrease} 
          disabled={minDisabled}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-90"
        > 
          <Minus className="w-4 h-4" /> 
        </button>
        
        <span className="text-sm font-mono font-bold text-white tabular-nums w-[56px] text-center select-none">
          {value}
        </span>
        
        <button 
          onClick={onIncrease} 
          disabled={maxDisabled}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-90"
        > 
          <Plus className="w-4 h-4" /> 
        </button>
      </div>
    </div>
  );
}

export function UnifiedControlBar({
  highlightDuration,
  fontSize,
  lineHeight,
  horizontalPadding,
  menuOpacity,
  onHighlightDurationChange,
  onFontSizeChange,
  onLineHeightChange,
  onHorizontalPaddingChange,
  onMenuOpacityChange,
  isPlaying,
  onTogglePlay,
  recordingState,
  onStartRecording,
  onStopRecording,
  devices,
  selectedDeviceId,
  selectedDeviceLabel,
  onMicrophoneChange,
  audioFormat,
  audioQuality,
  onFormatChange,
  onQualityChange,
  onDownloadAudio,
  onPreviewRecording,
  onDiscardRecording,
  hasRecording = false,
  audioBlob: passedAudioBlob,
  recordingDuration: passedDuration = 0,
  isConverting = false,
  isExpanded = true,
  activeMobileTab,
  onActiveMobileTabChange,
  isMobileMenuOpen: propIsMobileMenuOpen,
  onMobileMenuOpenChange,
  isPinned,
  onTogglePin,
}: UnifiedControlBarProps) {
  const { status, audioBlob: stateAudioBlob } = recordingState;
  const isRecording = status === 'recording';
  const { toast } = useToast();
  const { t } = useTranslation();
  
  // Internal State (Fallback if not controlled)
  const [internalMobileTab, setInternalMobileTab] = React.useState<'teleprompter' | 'audio'>('teleprompter');
  const [internalIsMobileMenuOpen, setInternalIsMobileMenuOpen] = React.useState(false);

  // Effective State
  const mobileTab = activeMobileTab ?? internalMobileTab;
  const isMobileMenuOpen = propIsMobileMenuOpen ?? internalIsMobileMenuOpen;

  // Handlers
  const handleMobileTabChange = React.useCallback((tab: 'teleprompter' | 'audio') => {
    if (onActiveMobileTabChange) {
      onActiveMobileTabChange(tab);
    } else {
      setInternalMobileTab(tab);
    }
  }, [onActiveMobileTabChange]);

  const handleMobileMenuOpenChange = (open: boolean) => {
    if (onMobileMenuOpenChange) {
      onMobileMenuOpenChange(open);
    } else {
      setInternalIsMobileMenuOpen(open);
    }
  };

  // Sync mobile menu close with external state (e.g. play start)
  React.useEffect(() => {
    if (!isExpanded) {
      handleMobileMenuOpenChange(false);
    }
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Use passed audioBlob if available, otherwise fallback to recordingState
  const audioBlob = passedAudioBlob || stateAudioBlob;
  const recordingDuration = passedDuration || recordingState.duration || 0;

  const [showMicMenu, setShowMicMenu] = React.useState(false);
  const [showFormatMenu, setShowFormatMenu] = React.useState(false);

  // Audio playback state for audio tab
  const [isAudioPlaying, setIsAudioPlaying] = React.useState(false);
  const [audioCurrentTime, setAudioCurrentTime] = React.useState(0);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  
  // Create audio URL when blob is available
  React.useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAudioUrl(null);
    }
  }, [audioBlob]);
  
  // Auto-switch to audio tab when recording stops with audio
  React.useEffect(() => {
    if (status === 'stopped' && audioBlob) {
      handleMobileTabChange('audio');
    }
  }, [status, audioBlob, handleMobileTabChange]);
  
  const toggleAudioPlayback = () => {
    if (!audioRef.current) return;
    if (isAudioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsAudioPlaying(!isAudioPlaying);
  };
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Button state management
  type ButtonState = 'initial' | 'processing' | 'complete' | 'ready';
  const [downloadState, setDownloadState] = React.useState<ButtonState>('initial');
  const [showSaveDialog, setShowSaveDialog] = React.useState<'download' | null>(null);
  
  // Cloud recordings hook
  const { 
    uploading: cloudUploading, 
    uploadProgress: cloudUploadProgress,
    uploadRecording 
  } = useRecordings();
  
  // FFmpeg for format conversion before cloud upload
  const { convert: convertAudio, status: ffmpegConvertStatus } = useFFmpeg();
  const [cloudConversionProgress, setCloudConversionProgress] = React.useState(0);
  
  // Cloud save state
  const [cloudSaveState, setCloudSaveState] = React.useState<'idle' | 'converting' | 'uploading' | 'done' | 'error'>('idle');
  const [showCloudUploadDialog, setShowCloudUploadDialog] = React.useState(false);
  const [uploadFilename, setUploadFilename] = React.useState("");
  
  // Handle download button click
  const handleDownloadClick = () => {
    if (downloadState === 'initial') {
      // Start conversion
      setDownloadState('processing');
      onDownloadAudio(); // Trigger actual conversion
    } else if (downloadState === 'ready') {
      // Show save location dialog
      setShowSaveDialog('download');
    }
  };
  
  // Watch isConverting to update download state
  React.useEffect(() => {
    if (downloadState === 'processing' && !isConverting) {
      // Conversion complete
      setDownloadState('complete');
      setTimeout(() => setDownloadState('ready'), 1000);
    }
  }, [isConverting, downloadState]);
  
  // Handle cloud save button click - OPEN DIALOG
  const handleCloudSave = () => {
    if (!audioBlob) {
      toast({
        title: t('teleprompter.errors.saveFailed'),
        description: t('teleprompter.errors.noRecording'),
        variant: "destructive",
      });
      return;
    }
    
    // Set default filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    setUploadFilename(`recording-${timestamp}`);
    setShowCloudUploadDialog(true);
  };

  // Execute upload after name confirmation
  const executeCloudUpload = async () => {
    if (!audioBlob) return;
    
    // Sanitize filename: remove slashes, backslashes and other risky chars
    let safeFilename = uploadFilename.trim()
      .replace(/[/\\]/g, '-')  // Replace slashes with dash
      .replace(/[<>:"|?*]/g, ''); // Remove other invalid filesystem chars
      
    if (!safeFilename) {
       const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
       safeFilename = `recording-${timestamp}`;
    }
    
    setShowCloudUploadDialog(false);
    
    // Ensure extension
    const finalFilename = safeFilename.endsWith(`.${audioFormat}`) 
      ? safeFilename 
      : `${safeFilename}.${audioFormat}`;
      
    console.log('[UnifiedControlBar] Cloud upload starting:', { finalFilename, format: audioFormat });
    
    let blobToUpload = audioBlob;
    
    // ========= FORMAT CONVERSION (if not webm) =========
    if (audioFormat !== 'webm') {
      setCloudSaveState('converting');
      setCloudConversionProgress(0);
      
      try {
        console.log('[UnifiedControlBar] Converting to', audioFormat);
        const convertedBlob = await convertAudio(audioBlob, {
          format: audioFormat,
          quality: audioQuality,
        }, (progress) => {
          setCloudConversionProgress(progress);
        });
        
        if (convertedBlob) {
          blobToUpload = convertedBlob;
          console.log('[UnifiedControlBar] Conversion complete:', {
            originalSize: audioBlob.size,
            convertedSize: convertedBlob.size,
            type: convertedBlob.type
          });
        } else {
          throw new Error(t('teleprompter.errors.formatConvertFailed', 'Format Conversion Failed'));
        }
      } catch (err) {
        console.error('[UnifiedControlBar] Conversion error:', err);
        
        // On mobile, FFmpeg may fail. Offer to upload original WebM as fallback
        const fallbackConfirmed = window.confirm(
          `格式轉換失敗：${err instanceof Error ? err.message : '未知錯誤'}\n\n是否改為上傳原始 WebM 格式？`
        );
        
        if (fallbackConfirmed) {
          // Fallback to original blob
          blobToUpload = audioBlob;
          // Update filename to use .webm extension
          const webmFilename = safeFilename.endsWith('.webm') 
            ? safeFilename 
            : `${safeFilename}.webm`;
          
          // Continue to upload section with webm format
          setCloudSaveState('uploading');
          
          const result = await uploadRecording(blobToUpload, {
            format: 'webm',
            duration: recordingDuration,
            filename: webmFilename
          });
          
          if (result) {
            setCloudSaveState('done');
            setTimeout(() => setCloudSaveState('idle'), 3000);
          } else {
            setCloudSaveState('error');
            setTimeout(() => setCloudSaveState('idle'), 3000);
          }
          return; // Exit early after fallback upload
        } else {
          toast({
            title: t('teleprompter.errors.convertFailed'),
            description: err instanceof Error ? err.message : t('teleprompter.errors.formatConvertFailed'),
            variant: 'destructive',
          });
          setCloudSaveState('error');
          setTimeout(() => setCloudSaveState('idle'), 3000);
          return;
        }
      }
    }
    
    // ========= UPLOAD =========
    setCloudSaveState('uploading');
    
    const result = await uploadRecording(blobToUpload, {
      format: audioFormat,
      duration: recordingDuration,
      filename: finalFilename
    });
    
    if (result) {
      setCloudSaveState('done');
      setTimeout(() => setCloudSaveState('idle'), 3000);
    } else {
      setCloudSaveState('error');
      setTimeout(() => setCloudSaveState('idle'), 3000);
    }
  };
  
  // Handle save location selection
  const handleSaveLocation = (location: 'local' | 'cloud') => {
    if (location === 'local') {
      if (showSaveDialog === 'download') {
        toast({
          title: t('teleprompter.hints.downloadStarted'),
          description: t('teleprompter.hints.fileSavedLocally'),
        });
      }
    } else {
      toast({
        title: t('teleprompter.hints.cloudDeveloping'),
        description: t('teleprompter.hints.featureComingSoon'),
      });
    }
    setShowSaveDialog(null);
  };
  
  // Reset states when recording changes
  React.useEffect(() => {
    if (status === 'idle') {
      setDownloadState('initial');
    }
  }, [status]);

  return (
    <>
    {/* ================= DESKTOP VIEW ================= */}
    <div className={`
      hidden xl:block
      fixed bottom-0 left-0 right-0
      transition-all duration-500 ease-in-out
      z-50
      ${isExpanded 
        ? 'bg-zinc-900 border-t-2 border-white px-4 py-6 shadow-[0_-4px_0_rgba(0,0,0,0.5)]' 
        : 'bg-transparent border-t-0 pointer-events-none px-4 py-6' /* Mini Mode: Container transparent, no clicks */
      }
    `}>
      {/* 3-column flex layout: left-center-right */}
      <div className="w-full flex flex-wrap items-center justify-between gap-4">
        {/* 左側區域 - 參數調整控制項 */}
        <div className={`
            flex-1 min-w-[320px] flex justify-start items-center pl-4 transition-all duration-500
            ${isExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}>
          <div className="w-full grid grid-cols-2 gap-x-2 gap-y-2">
            <ControlItem
              icon={Gauge}
              label={t('teleprompter.controls.speed')}
              value={`${highlightDuration.toFixed(1)}s`}
              onDecrease={() => onHighlightDurationChange(Math.max(0.5, highlightDuration - 0.5))}
              onIncrease={() => onHighlightDurationChange(Math.min(10, highlightDuration + 0.5))}
              minDisabled={highlightDuration <= 0.5}
              maxDisabled={highlightDuration >= 10}
            />
            <ControlItem
              icon={Type}
              label={t('teleprompter.controls.fontSize')}
              value={`${fontSize}px`}
              onDecrease={() => onFontSizeChange(-4)}
              onIncrease={() => onFontSizeChange(4)}
              minDisabled={fontSize <= 16}
              maxDisabled={fontSize >= 120}
            />
            <ControlItem
              icon={AlignJustify}
              label={t('teleprompter.controls.lineHeight')}
              value={lineHeight.toFixed(1)}
              onDecrease={() => onLineHeightChange(Math.max(1, lineHeight - 0.2))}
              onIncrease={() => onLineHeightChange(Math.min(3, lineHeight + 0.2))}
              minDisabled={lineHeight <= 1}
              maxDisabled={lineHeight >= 3}
            />
            <ControlItem
              icon={ArrowRightLeft}
              label={t('teleprompter.controls.margin')}
              value={`${horizontalPadding}%`}
              onDecrease={() => onHorizontalPaddingChange(Math.max(0, horizontalPadding - 5))}
              onIncrease={() => onHorizontalPaddingChange(Math.min(40, horizontalPadding + 5))}
              minDisabled={horizontalPadding <= 0}
              maxDisabled={horizontalPadding >= 40}
            />
          </div>
        </div>

        {/* ═══ 中間區域：播放控制 (flex-shrink-0 保持固定寬度) ═══ */}
        <div className="flex-shrink-0 px-4 flex items-center gap-6 z-20 pointer-events-auto">
          
          {/* 釘選按鈕 (Pin Controls) */}
          <button
            onClick={onTogglePin}
            className={`
              w-12 h-12 flex items-center justify-center
              rounded-xl border-2 border-white
              shadow-[2px_2px_0_white] transition-all duration-150
              hover:-translate-y-0.5 hover:shadow-[4px_4px_0_white]
              active:translate-y-0.5 active:shadow-none
              ${isPinned 
                ? 'bg-[#FFD93D] text-black hover:bg-[#ffdf66]' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
              }
            `}
            title={isPinned ? t('common.unpin', 'Unpin') : t('common.pin', 'Pin')}
          >
            <Pin className={`w-5 h-5 ${isPinned ? 'fill-current' : ''}`} />
          </button>

          {/* 播放按鈕 (Left Center) */}
          <button
            onClick={() => {
              if (status === 'idle') {
                onStartRecording();
              }
              onTogglePlay();
            }}
            className={`
              w-[72px] h-[72px] flex items-center justify-center
              rounded-full border-2 border-white
              shadow-[4px_4px_0_white] transition-all duration-150
              hover:-translate-y-1 hover:shadow-[8px_8px_0_white]
              active:translate-y-0.5 active:shadow-none
              hover:bg-pink-400
              ${isPlaying 
                ? 'bg-[#6FEDD6] border-white text-black' 
                : 'bg-[#FF6B9D] text-black'
              }
            `}
          >
            {isPlaying ? (
              <Pause className="w-9 h-9 fill-current" />
            ) : (
              <Play className="w-9 h-9 fill-current ml-1" />
            )}
          </button>
          
          {/* 停止按鈕 (Right Center) */}
          <button
            onClick={onStopRecording}
            disabled={!isRecording && status !== 'paused'} // Allow stop if paused too
            className={`
              w-12 h-12 flex items-center justify-center
              rounded-xl border-2 border-white
              shadow-[4px_4px_0_white] transition-all duration-150
              hover:-translate-y-1 hover:shadow-[6px_6px_0_white]
              active:translate-y-0.5 active:shadow-none
              ${(isRecording || status === 'paused')
                ? 'bg-red-500 text-white cursor-pointer hover:bg-red-600' 
                : 'bg-zinc-800 text-zinc-500 border-zinc-600 shadow-none'
              }
            `}
          >
            <Square className="w-4 h-4 fill-current" />
          </button>

        </div>

        {/* ═══ 右側區域：錄音設定 (Collapse in Mini Mode) ═══ */}
        <div className={`
            flex-1 min-w-[360px] flex justify-end items-center pr-4 transition-all duration-500
            ${isExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
        `}>
          <div className="grid grid-cols-3 gap-x-3 gap-y-3">
            {/* 1. Feature Selectors (Mic & Format) */}
            
            {/* Mic Selection */}
            <div className="relative">
              <SelectButton
                icon={Mic}
                label={selectedDeviceLabel || t('teleprompter.controls.selectMic')}
                onClick={() => setShowMicMenu(!showMicMenu)}
                disabled={isRecording}
              />
              {showMicMenu && (
                <div className="
                  absolute bottom-full mb-2 left-0
                  bg-zinc-900
                  border-2 border-white rounded-xl
                  py-2 min-w-[280px] shadow-[4px_4px_0_white]
                  z-50
                ">
                  {devices.map((device) => (
                    <button
                      key={device.deviceId}
                      onClick={() => {
                        onMicrophoneChange(device.deviceId);
                        setShowMicMenu(false);
                      }}
                      className={`
                        w-full px-4 py-2 text-left text-sm whitespace-nowrap
                        hover:bg-white/10 transition-colors
                        ${selectedDeviceId === device.deviceId 
                          ? 'text-[#FF6B9D] bg-white/5' 
                          : 'text-gray-300'
                        }
                      `}
                    >
                      {device.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Format Selection */}
            <div className="relative">
              <SelectButton
                icon={FileAudio}
                label={isConverting ? '...' : t('teleprompter.controls.format')}
                onClick={() => setShowFormatMenu(!showFormatMenu)}
                disabled={isRecording || isConverting}
              />
              {showFormatMenu && (
                <div className="
                  absolute bottom-full mb-2 right-0
                  bg-zinc-900
                  border-2 border-white rounded-xl
                  py-2 min-w-[160px] shadow-[4px_4px_0_white]
                  z-50
                ">
                  <div className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t('teleprompter.controls.format')}</div>
                  {(['webm', 'mp3', 'wav', 'aac'] as AudioFormat[]).map((format) => (
                    <button
                      key={format}
                      onClick={() => {
                        onFormatChange(format);
                      }}
                      className={`
                        w-full px-4 py-2 text-left text-sm whitespace-nowrap
                        hover:bg-white/10 transition-colors
                        ${audioFormat === format 
                          ? 'text-[#FF6B9D] bg-white/5' 
                          : 'text-gray-300'
                        }
                      `}
                    >
                      {format.toUpperCase()}
                    </button>
                  ))}
                  
                  {audioFormat !== 'webm' && (
                    <>
                      <div className="h-px bg-white/10 my-2" />
                      <div className="px-3 py-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t('teleprompter.controls.quality')}</div>
                      {(['standard', 'high', 'lossless'] as AudioQuality[]).map((q) => (
                        <button
                          key={q}
                          onClick={() => {
                            onQualityChange(q);
                            setShowFormatMenu(false);
                          }}
                          disabled={q === 'lossless' && audioFormat !== 'wav'}
                          className={`
                            w-full px-4 py-2 text-left text-xs font-medium
                            hover:bg-white/10 transition-colors
                            ${audioQuality === q 
                              ? 'bg-[#6FEDD6]/20 text-[#6FEDD6]' 
                              : 'text-gray-300'
                            }
                            disabled:opacity-30 disabled:cursor-not-allowed
                          `}
                        >
                          {q === 'standard' ? t('teleprompter.quality.standard') : q === 'high' ? t('teleprompter.quality.high') : t('teleprompter.quality.lossless')}
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 2. Primary Actions Row: Preview & Download */}
             <button
               onClick={onPreviewRecording}
               disabled={!hasRecording || !onPreviewRecording}
               className="
                 h-[34px] flex items-center justify-center gap-1.5 px-3
                 bg-[#FF6B9D] text-black
                 border-2 border-white
                 rounded-lg font-bold text-xs whitespace-nowrap
                 hover:translate-x-0.5 hover:translate-y-0.5
                 shadow-[2px_2px_0_white]
                 active:translate-x-1 active:translate-y-1 active:shadow-none
                 transition-all
                 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:transform-none disabled:active:transform-none
               "
             >
               <Ear className="w-4 h-4" />
               {t('teleprompter.tools.preview')}
            </button>
            
            <button
              onClick={onDownloadAudio}
              disabled={!hasRecording || isConverting}
              className={`
                h-[34px] flex items-center justify-center gap-1.5 px-3
                border-2 border-white cursor-pointer
                rounded-lg font-bold text-xs whitespace-nowrap
                hover:translate-x-0.5 hover:translate-y-0.5
                shadow-[2px_2px_0_white]
                active:translate-x-1 active:translate-y-1 active:shadow-none
                transition-all
                disabled:opacity-40 disabled:cursor-not-allowed
                ${downloadState === 'complete' 
                   ? 'bg-[#6FEDD6] text-black' 
                   : 'bg-[#4DA6FF] text-black'
                }
              `}
            >
              {downloadState === 'processing' ? (
                 <Loader2 className="w-4 h-4 animate-spin" />
              ) : downloadState === 'complete' ? (
                 <Check className="w-4 h-4" />
              ) : (
                 <Download className="w-4 h-4" />
              )}
              {downloadState === 'processing' ? t('teleprompter.tools.converting') : t('teleprompter.tools.download')}
            </button>

            {/* Cloud Save Button */}
            <button
               onClick={handleCloudSave}
               disabled={!hasRecording || cloudSaveState === 'converting' || cloudSaveState === 'uploading' || cloudSaveState === 'done'}
                className={`
                  h-[34px] flex items-center justify-center gap-1.5 px-3
                  border-2 border-white
                  rounded-lg font-bold text-xs whitespace-nowrap
                  hover:translate-x-0.5 hover:translate-y-0.5
                  shadow-[2px_2px_0_white]
                  active:translate-x-1 active:translate-y-1 active:shadow-none
                  transition-all
                  disabled:opacity-40 disabled:cursor-not-allowed
                  ${cloudSaveState === 'done' 
                    ? 'bg-[#6FEDD6]' 
                    : cloudSaveState === 'error'
                      ? 'bg-red-400'
                      : cloudSaveState === 'converting'
                        ? 'bg-[#4DA6FF]'
                        : 'bg-[#FFD93D]'
                  } text-black
                `}
             >
               {cloudSaveState === 'converting' ? (
                 <Loader2 className="w-4 h-4 animate-spin" />
               ) : cloudSaveState === 'uploading' ? (
                 <Loader2 className="w-4 h-4 animate-spin" />
               ) : cloudSaveState === 'done' ? (
                 <Check className="w-4 h-4" />
               ) : cloudSaveState === 'error' ? (
                 <X className="w-4 h-4" />
               ) : (
                 <CloudUpload className="w-4 h-4" />
               )}
               {cloudSaveState === 'converting' 
                 ? t('teleprompter.cloud.converting', { percent: Math.round(cloudConversionProgress) })
                 : cloudSaveState === 'uploading' 
                   ? t('teleprompter.cloud.uploading')
                   : cloudSaveState === 'done' 
                     ? t('teleprompter.cloud.done')
                     : t('teleprompter.tools.cloud')}
            </button>


          </div>
        </div>
      </div>
    </div>

    {/* ================= MOBILE MINI BAR (Persistent Bottom Bar) ================= */}
    <div className={`
        xl:hidden
        fixed bottom-0 left-0 right-0
        px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3
        bg-zinc-950/90 backdrop-blur-md
        border-t border-white/10
        z-40
        transition-transform duration-300
        ${isMobileMenuOpen ? 'translate-y-[110%]' : 'translate-y-0'}
    `}>
        <div className="flex items-center justify-between gap-4">
            
            {/* Scroll Speed Control */}
            <div className="flex items-center gap-1 bg-zinc-900/80 p-1 rounded-xl border border-white/10">
                 <button 
                   onClick={(e) => {
                     e.stopPropagation();
                     onHighlightDurationChange(Math.max(0.5, highlightDuration - 0.5));
                   }}
                   disabled={highlightDuration <= 0.5}
                   className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white rounded-lg active:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  > 
                    <Minus className="w-3.5 h-3.5" /> 
                 </button>
                 <div className="flex flex-col items-center w-8">
                    <span className="text-[9px] text-zinc-500 uppercase font-bold leading-none mb-0.5">SPD</span>
                    <span className="text-sm font-mono font-bold text-[#FFD93D] tabular-nums leading-none">
                        {highlightDuration.toFixed(1)}
                    </span>
                 </div>
                 <button 
                   onClick={(e) => {
                     e.stopPropagation();
                     onHighlightDurationChange(Math.min(10, highlightDuration + 0.5));
                   }} 
                   disabled={highlightDuration >= 10}
                   className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white rounded-lg active:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  > 
                    <Plus className="w-3.5 h-3.5" /> 
                 </button>
            </div>

            {/* Play/Pause Control */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    if (status === 'idle') onStartRecording();
                    onTogglePlay();
                }}
                className={`
                    w-12 h-12 flex items-center justify-center rounded-full
                    shadow-lg active:scale-95 transition-all
                    ${isPlaying
                        ? 'bg-zinc-100 text-zinc-900'
                        : 'bg-[#FF6B9D] text-black shadow-[0_0_15px_rgba(255,107,157,0.4)]'
                    }
                `}
            >
                {isPlaying ? (
                    <Pause className="w-5 h-5 fill-current" />
                ) : (
                    <Play className="w-5 h-5 fill-current ml-0.5" />
                )}
            </button>

            {/* Stop / Settings Toggle */}
            <div className="flex items-center gap-2 w-28 justify-end">
                {(isRecording || status === 'paused') ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onStopRecording();
                        }}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 text-red-500 border border-red-500/50 active:scale-90 transition-all"
                    >
                        <Square className="w-4 h-4 fill-current" />
                    </button>
                ) : (
                   <div className="w-10" /> /* Placeholder to ease alignment */
                )}
                
                {/* Expand Drawer Button */}
                 <button
                    onClick={() => handleMobileMenuOpenChange(true)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-zinc-800 text-zinc-400 hover:text-white active:bg-zinc-700 transition-all"
                >
                    <Settings2 className="w-5 h-5" />
                </button>
            </div>
        </div>
    </div>

    {/* ================= MOBILE EXPANDED DRAWER ================= */}
    
    {/* Backdrop - Click outside to close */}
    {isMobileMenuOpen && (
      <div 
        className="xl:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
        onClick={() => handleMobileMenuOpenChange(false)}
      />
    )}

    <div className={`
        xl:hidden
        fixed bottom-0 left-0 right-0
        max-h-[85dvh] h-auto
        bg-zinc-950/95 backdrop-blur-xl
        border-t border-white/10
        rounded-t-2xl
        shadow-[-10px_-10px_30px_rgba(0,0,0,0.5)]
        z-50
        transition-transform duration-500 cubic-bezier(0.32, 0.72, 0, 1)
        ${isMobileMenuOpen ? 'translate-y-0' : 'translate-y-[110%]'}
    `}>
        {/* Handle Bar */}
        <div 
            className="w-full h-10 flex items-center justify-center cursor-pointer active:opacity-70 group shrink-0"
            onClick={() => handleMobileMenuOpenChange(false)}
        >
             <ChevronUp className="w-6 h-6 text-zinc-600 rotate-180" />
        </div>

        {/* Content Container */}
        <div className="h-full px-5 pt-0 pb-[calc(2rem+env(safe-area-inset-bottom))] flex flex-col overflow-y-auto">
            
            {/* Tab Navigation */}
            <div className="flex border-b border-zinc-800 mb-4 -mx-5 px-5">
              <button
                onClick={() => handleMobileTabChange('teleprompter')}
                className={`
                  flex-1 py-3 text-sm font-bold transition-all border-b-2
                  ${mobileTab === 'teleprompter'
                    ? 'text-white border-[#FF6B9D]'
                    : 'text-zinc-500 border-transparent'
                  }
                `}
              >
                {t('teleprompter.tabs.teleprompter')}
              </button>
              <button
                onClick={() => handleMobileTabChange('audio')}
                className={`
                  flex-1 py-3 text-sm font-bold transition-all border-b-2 relative
                  ${mobileTab === 'audio'
                    ? 'text-white border-[#6FEDD6]'
                    : 'text-zinc-500 border-transparent'
                  }
                `}
              >
                {t('teleprompter.tabs.audio')}
                {/* Green dot indicator */}
                {hasRecording && mobileTab !== 'audio' && (
                  <span className="absolute top-2 right-1/4 h-2 w-2 rounded-full bg-[#6FEDD6]" />
                )}
              </button>
            </div>

            {/* Tab Content - Fixed height container for consistent layout */}
            <div className="h-[480px] flex flex-col">
            {mobileTab === 'teleprompter' ? (
            <>

            {/* 2. Transport Deck (Unified: Mic - Play - Stop - Format) */}
            <div className="flex-none flex items-center justify-center gap-4 relative py-6 mb-4">
                
                {/* 1. Mic Button (Left of Play) */}
                <div className="relative z-30 mr-3">
                     <button 
                        onClick={() => setShowMicMenu(!showMicMenu)}
                        disabled={isRecording}
                        className={`
                            w-14 h-14 flex items-center justify-center rounded-2xl border-2 transition-all active:scale-95
                            ${selectedDeviceId ? 'bg-zinc-800 border-zinc-700 text-[#6FEDD6]' : 'bg-zinc-900 border-zinc-800 text-zinc-500'}
                        `}
                    >
                        <Mic className="w-6 h-6" />
                    </button>

                    {/* Mic Menu Pop-up (Positioned below) */}
                    {showMicMenu && (
                        <div className="absolute top-full left-0 mt-3 w-[260px] bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl z-[60]">
                             <div className="px-4 py-3 bg-zinc-950/50 border-b border-zinc-800 text-xs font-bold text-zinc-500 uppercase tracking-wider">Select Input Device</div>
                             <div className="max-h-[200px] overflow-y-auto p-1">
                                {devices.map((device) => (
                                    <button
                                    key={device.deviceId}
                                    onClick={() => { onMicrophoneChange(device.deviceId); setShowMicMenu(false); }}
                                    className={`
                                        w-full px-4 py-3 text-left text-sm font-medium rounded-xl transition-colors mb-1
                                        ${selectedDeviceId === device.deviceId ? 'bg-[#6FEDD6] text-black' : 'text-zinc-300 active:bg-zinc-800 hover:bg-zinc-800/50'}
                                    `}
                                    >
                                    {device.label || `Microphone ${device.deviceId.slice(0,4)}`}
                                    </button>
                                ))}
                             </div>
                        </div>
                    )}
                </div>

                {/* 2. Play Button - Circular */}
                <button
                    onClick={() => {
                        if (status === 'idle') onStartRecording();
                        onTogglePlay();
                    }}
                    className={`
                        relative z-10 w-20 h-20 flex items-center justify-center rounded-full border-4
                        shadow-[0_0_50px_-10px_var(--shadow-color)] active:scale-95 transition-all
                        ${isPlaying 
                            ? 'bg-zinc-100 border-white text-zinc-900 [--shadow-color:rgba(255,255,255,0.3)]' 
                            : 'bg-[#FF6B9D] border-[#FF6B9D]/50 text-black [--shadow-color:rgba(255,107,157,0.4)]'
                        }
                    `}
                >
                     {isPlaying ? (
                        <Pause className="w-8 h-8 fill-current" />
                     ) : (
                        <Play className="w-8 h-8 fill-current ml-1" />
                     )}
                </button>

                {/* 3. Stop Button - Same size as mic, pushed right */}
                <button
                    onClick={onStopRecording}
                    disabled={!isRecording && status !== 'paused'}
                    className={`
                        ml-3 w-14 h-14 flex items-center justify-center rounded-2xl active:scale-95 transition-all
                        ${(isRecording || status === 'paused')
                            ? 'bg-red-500 text-white shadow-[0_0_30px_-5px_rgba(239,68,68,0.4)] cursor-pointer' 
                            : 'bg-zinc-900 text-zinc-700 cursor-not-allowed opacity-50'
                        }
                    `}
                >
                    <Square className="w-6 h-6 fill-current" />
                </button>
            </div>

            {/* 3. Settings Control Stack (Horizontal Rows) - Centered */}
            <div className="flex-none flex flex-col items-center gap-3 mb-6">
                <MobileControlRow
                    icon={Gauge}
                    label={t('teleprompter.controls.speed')}
                    value={`${highlightDuration.toFixed(1)}s`}
                    onDecrease={() => onHighlightDurationChange(Math.min(10, highlightDuration + 0.5))}
                    onIncrease={() => onHighlightDurationChange(Math.max(0.5, highlightDuration - 0.5))}
                    minDisabled={highlightDuration >= 10}
                    maxDisabled={highlightDuration <= 0.5}
                    color="#FFD93D"
                />
                <MobileControlRow
                    icon={Type}
                    label={t('teleprompter.controls.fontSize')}
                    value={`${fontSize}`}
                    onDecrease={() => onFontSizeChange(-4)}
                    onIncrease={() => onFontSizeChange(4)}
                    minDisabled={fontSize <= 16}
                    maxDisabled={fontSize >= 120}
                    color="#6FEDD6"
                />
                <MobileControlRow
                    icon={AlignJustify}
                    label={t('teleprompter.controls.lineHeight')}
                    value={lineHeight.toFixed(1)}
                    onDecrease={() => onLineHeightChange(Math.max(1, lineHeight - 0.2))}
                    onIncrease={() => onLineHeightChange(Math.min(3, lineHeight + 0.2))}
                    minDisabled={lineHeight <= 1}
                    maxDisabled={lineHeight >= 3}
                    color="#FF6B9D"
                />
                <MobileControlRow
                    icon={ArrowRightLeft}
                    label={t('teleprompter.controls.margin')}
                    value={`${horizontalPadding}`}
                    onDecrease={() => onHorizontalPaddingChange(Math.max(0, horizontalPadding - 5))}
                    onIncrease={() => onHorizontalPaddingChange(Math.min(40, horizontalPadding + 5))}
                    minDisabled={horizontalPadding <= 0}
                    maxDisabled={horizontalPadding >= 40}
                    color="#4DA6FF"
                />
            </div>
            </>
            ) : (
              /* Audio Tab Content - Always visible controls */
              <div className="flex flex-col gap-4 h-full">
                {/* Hidden Audio Element */}
                {audioUrl && (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={(e) => setAudioCurrentTime(e.currentTarget.currentTime)}
                    onEnded={() => setIsAudioPlaying(false)}
                    onLoadedMetadata={(e) => {
                      // Duration is loaded
                    }}
                  />
                )}
                
                {/* 1. Playback Player - Always visible */}
                <div className={`bg-zinc-900 rounded-2xl border-2 border-zinc-800 p-4 ${!hasRecording ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    {/* Play/Pause Button */}
                    <button
                      onClick={toggleAudioPlayback}
                      disabled={!hasRecording || !audioUrl}
                      className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#FF6B9D] text-black active:scale-95 transition-all shrink-0 disabled:opacity-50 disabled:active:scale-100"
                    >
                      {isAudioPlaying ? (
                        <Pause className="w-5 h-5 fill-current" />
                      ) : (
                        <Play className="w-5 h-5 fill-current ml-0.5" />
                      )}
                    </button>
                    
                    {/* Time Display */}
                    <div className="flex items-center gap-2 text-sm font-mono shrink-0">
                      <span className="text-white">{hasRecording ? formatTime(audioCurrentTime) : '0:00'}</span>
                      <span className="text-zinc-600">/</span>
                      <span className="text-zinc-400">{hasRecording ? formatTime(recordingDuration || 0) : '0:00'}</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#FF6B9D] transition-all duration-100"
                        style={{ 
                          width: hasRecording && recordingDuration 
                            ? `${(audioCurrentTime / recordingDuration) * 100}%` 
                            : '0%' 
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Status Text */}
                  {!hasRecording && (
                    <p className="text-xs text-zinc-500 text-center mt-2">{t('teleprompter.tabs.noRecording')}</p>
                  )}
                </div>
                
                {/* 2. Format & Quality Selection - Always visible */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Format Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowFormatMenu(!showFormatMenu)}
                      disabled={!hasRecording || isConverting}
                      className="w-full h-12 flex items-center justify-between px-4 bg-zinc-900 rounded-xl border-2 border-zinc-800 text-white font-medium active:scale-[0.98] transition-all disabled:opacity-50"
                    >
                      <span className="text-zinc-500 text-sm">{t('teleprompter.controls.format')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[#FFD93D] font-bold text-xs uppercase">{audioFormat}</span>
                        <ChevronDown className="w-4 h-4 text-zinc-500" />
                      </div>
                    </button>
                    {showFormatMenu && hasRecording && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border-2 border-zinc-800 rounded-xl overflow-hidden z-50 p-1">
                        {(['webm', 'mp3', 'wav'] as AudioFormat[]).map((f) => (
                          <button
                            key={f}
                            onClick={() => { onFormatChange(f); setShowFormatMenu(false); }}
                            className={`w-full px-4 py-2.5 text-xs font-bold uppercase rounded-lg transition-all ${
                              audioFormat === f ? 'bg-[#FFD93D] text-black' : 'text-zinc-400 active:bg-zinc-800'
                            }`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Quality Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        if (audioFormat !== 'webm' && hasRecording) {
                          setShowMicMenu(!showMicMenu);
                        }
                      }}
                      disabled={audioFormat === 'webm' || isConverting || !hasRecording}
                      className={`w-full h-12 flex items-center justify-between px-4 bg-zinc-900 rounded-xl border-2 border-zinc-800 text-white font-medium active:scale-[0.98] transition-all disabled:opacity-50`}
                    >
                      <span className="text-zinc-500 text-sm">{t('teleprompter.controls.quality')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[#6FEDD6] font-bold text-xs">
                          {audioFormat === 'webm' ? '-' : audioQuality === 'standard' ? '128k' : audioQuality === 'high' ? '256k' : t('teleprompter.quality.lossless')}
                        </span>
                        <ChevronDown className="w-4 h-4 text-zinc-500" />
                      </div>
                    </button>
                    {showMicMenu && audioFormat !== 'webm' && hasRecording && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-900 border-2 border-zinc-800 rounded-xl overflow-hidden z-50 p-1">
                        {(['standard', 'high', 'lossless'] as AudioQuality[]).map((q) => (
                          <button
                            key={q}
                            onClick={() => { onQualityChange(q); setShowMicMenu(false); }}
                            disabled={q === 'lossless' && audioFormat !== 'wav'}
                            className={`w-full px-4 py-2.5 text-xs font-bold rounded-lg transition-all ${
                              audioQuality === q ? 'bg-[#6FEDD6] text-black' : 'text-zinc-400 active:bg-zinc-800'
                            } disabled:opacity-30`}
                          >
                            {q === 'standard' ? t('teleprompter.quality.standard') : q === 'high' ? t('teleprompter.quality.high') : t('teleprompter.quality.lossless')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 3. Action Buttons - Always visible */}
                <div className="flex flex-col flex-1 gap-2">
                  {/* Download - 狀態切換 */}
                  <button
                    onClick={handleDownloadClick}
                    disabled={!hasRecording || downloadState === 'processing' || downloadState === 'complete'}
                    className={`h-12 flex items-center justify-center gap-2 font-bold rounded-xl border-2 border-black active:translate-y-0.5 active:shadow-none shadow-[3px_3px_0_black] transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 ${
                      downloadState === 'complete' 
                        ? 'bg-[#6FEDD6] text-black' 
                        : 'bg-[#4DA6FF] text-black'
                    }`}
                  >
                    {downloadState === 'processing' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t('teleprompter.tools.converting')}
                      </>
                    ) : downloadState === 'complete' ? (
                      <>
                        <Check className="w-5 h-5" />
                        {t('teleprompter.tools.conversionComplete')}
                      </>
                    ) : downloadState === 'ready' ? (
                      <>
                        <Download className="w-5 h-5" />
                        {t('teleprompter.tools.downloadAudio')}
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        {t('teleprompter.tools.download')}
                      </>
                    )}
                  </button>
                  
                  {/* Cloud Save - 雲端保存 with inline dual-ring animation */}
                  <button
                    onClick={handleCloudSave}
                    disabled={!hasRecording || cloudSaveState === 'converting' || cloudSaveState === 'uploading' || cloudSaveState === 'done'}
                    className={`h-12 flex items-center justify-center gap-2 font-bold rounded-xl border-2 border-black active:translate-y-0.5 active:shadow-none shadow-[3px_3px_0_black] transition-all hover:brightness-110 disabled:cursor-not-allowed ${ 
                      cloudSaveState === 'done' 
                        ? 'bg-[#6FEDD6]' 
                        : cloudSaveState === 'error'
                          ? 'bg-red-400'
                          : 'bg-white'
                    } text-black`}
                  >
                    {(cloudSaveState === 'converting' || cloudSaveState === 'uploading') ? (
                      /* Inline Dual-Ring Progress Animation */
                      <div className="flex items-center gap-1">
                        {/* Ring 1: Converting - 淺灰底綠進度 */}
                        <div className="relative">
                          <MiniProgressRing 
                            progress={cloudSaveState === 'converting' ? cloudConversionProgress : 100} 
                            size={20} 
                            stroke={2.5}
                            trackColor="#D1D5DB"
                            color="#22C55E"
                          />
                          {cloudSaveState === 'uploading' && (
                            <Check className="w-2.5 h-2.5 absolute inset-0 m-auto text-[#22C55E]" strokeWidth={3} />
                          )}
                        </div>
                        {/* Connector Line - 深綠色，轉換完成後延伸 */}
                        <div className="w-6 h-0.5 bg-zinc-300 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#16A34A] rounded-full"
                            style={{
                              width: cloudSaveState === 'uploading' ? '100%' : '0%',
                              transition: 'width 1s ease-out'
                            }}
                          />
                        </div>
                        {/* Ring 2: Uploading - 淺灰底綠進度 */}
                        <div className="relative">
                          <MiniProgressRing 
                            progress={cloudSaveState === 'uploading' ? (cloudUploadProgress?.percentage ?? 0) : 0} 
                            size={20} 
                            stroke={2.5}
                            trackColor="#D1D5DB"
                            color="#22C55E"
                          />
                          {(cloudUploadProgress?.percentage ?? 0) >= 100 && (
                            <Check className="w-2.5 h-2.5 absolute inset-0 m-auto text-[#22C55E]" strokeWidth={3} />
                          )}
                        </div>
                        <span className="text-xs ml-1">
                          {cloudSaveState === 'converting' 
                            ? t('teleprompter.cloud.converting', { percent: Math.round(cloudConversionProgress) }) 
                            : t('teleprompter.cloud.uploading', { percent: Math.round(cloudUploadProgress?.percentage ?? 0) })}
                        </span>
                      </div>
                    ) : cloudSaveState === 'done' ? (
                      <>
                        <Check className="w-5 h-5" />
                        {t('teleprompter.cloud.saved')}
                      </>
                    ) : cloudSaveState === 'error' ? (
                      <>
                        <X className="w-5 h-5" />
                        {t('teleprompter.cloud.failed')}
                      </>
                    ) : (
                      <>
                        <CloudUpload className="w-5 h-5" />
                        {t('teleprompter.tools.cloud')}
                      </>
                    )}
                  </button>
                  
                  {/* Bottom buttons - 固定在底部 */}
                  <div className="flex flex-col gap-2 mt-auto pt-4">
                    {/* Discard - 描邊紅色 */}
                    <button
                      onClick={onDiscardRecording}
                      disabled={!hasRecording}
                      className="h-12 flex items-center justify-center gap-2 bg-transparent text-red-500 font-bold rounded-xl border-2 border-red-500 active:translate-y-0.5 transition-all hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-5 h-5" />
                      {t('teleprompter.preview.discard')}
                    </button>
                  </div>
                </div>
              </div>
            )}
            </div>
        </div>
    </div>
    
    {/* Save Location Dialog */}
    {showSaveDialog && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-zinc-900 rounded-2xl border-2 border-zinc-700 p-6 w-full max-w-sm shadow-2xl">
          {/* Header */}
          <h3 className="text-white font-bold text-lg text-center mb-6">
            {t('teleprompter.errors.cloudSaveTitle')}
          </h3>
          
          {/* Options */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {/* Local */}
            <button
              onClick={() => handleSaveLocation('local')}
              className="flex flex-col items-center gap-2 p-4 bg-white text-black font-bold rounded-xl border-2 border-black shadow-[3px_3px_0_black] active:translate-y-0.5 active:shadow-none transition-all hover:brightness-95"
            >
              <HardDrive className="w-8 h-8" />
              <span>本地</span>
            </button>
            
            {/* Cloud */}
            <button
              onClick={() => handleSaveLocation('cloud')}
              className="flex flex-col items-center gap-2 p-4 bg-[#6FEDD6] text-black font-bold rounded-xl border-2 border-black shadow-[3px_3px_0_black] active:translate-y-0.5 active:shadow-none transition-all hover:brightness-95"
            >
              <Cloud className="w-8 h-8" />
              <span>{t('teleprompter.tools.cloud')}</span>
            </button>
          </div>
          
          {/* Cancel */}
          <button
            onClick={() => setShowSaveDialog(null)}
            className="w-full py-3 text-zinc-400 font-medium hover:text-white transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    )}
    
      {/* Cloud Upload Dialog */}
      <Dialog open={showCloudUploadDialog} onOpenChange={setShowCloudUploadDialog}>
        <DialogContent className="bg-[#F5F0E8] border-2 border-black rounded-xl shadow-[8px_8px_0_#1A1A1A] sm:max-w-md p-6 gap-6">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-2xl font-bold">{t('teleprompter.cloud.uploadDialog.title')}</DialogTitle>
            <DialogDescription className="text-base text-gray-600">
              {t('teleprompter.cloud.uploadDialog.description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2">
            <div className="flex flex-col gap-3">
              <Label htmlFor="filename" className="text-base font-bold">
                {t('teleprompter.cloud.uploadDialog.fileName')}
              </Label>
              <Input
                id="filename"
                value={uploadFilename}
                onChange={(e) => setUploadFilename(e.target.value)}
                className="col-span-3 border-2 border-black rounded-lg h-12 text-lg focus-visible:ring-0 focus-visible:border-[#FF6B9D] focus-visible:ring-offset-0 bg-white"
                placeholder={t('teleprompter.cloud.uploadDialog.placeholder')}
              />
            </div>
          </div>
          
          <DialogFooter className="flex flex-row gap-3 sm:justify-end">
             <Button 
               variant="outline" 
               onClick={() => setShowCloudUploadDialog(false)}
               className="flex-1 sm:flex-none border-2 border-black rounded-lg h-12 font-bold hover:bg-gray-100 transition-colors"
             >
              {t('teleprompter.cloud.uploadDialog.cancel')}
            </Button>
            <Button 
              onClick={executeCloudUpload}
              className="flex-1 sm:flex-none bg-[#FF6B9D] text-black border-2 border-black rounded-lg h-12 font-bold hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all"
            >
              {t('teleprompter.cloud.uploadDialog.saveAndUpload')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cloud Upload Progress Indicator */}
      {cloudSaveState !== 'idle' && (
        <ProgressStepIndicator
          currentStep={cloudSaveState}
          progress={cloudSaveState === 'converting' ? cloudConversionProgress : (cloudUploadProgress?.percentage || 0)}
          onClose={() => setCloudSaveState('idle')}
        />
      )}
    </>
  );
}
